import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useCallback } from 'react';
import {
  LayoutGrid, Search, BookOpen, Printer,
  AlignLeft, GitCompare, Clock, Star,
  Palette, Eye, Settings, Layers, GraduationCap,
  Blend, Camera, ShieldCheck, Home, Droplet,
  Award, FileDown,
} from 'lucide-react';

export type NavDensity = 'sidebar' | 'compact' | 'tabs';

export type ToolId =
  | 'home'
  | 'formats' | 'nearest' | 'encyclopedia' | 'print'
  | 'semantic' | 'compare' | 'history' | 'favorites'
  | 'palette' | 'accessibility' | 'tokens' | 'education' | 'settings'
  | 'gradient' | 'extract' | 'audit' | 'substrate'
  | 'score' | 'export';

interface Tool {
  id: ToolId;
  label: string;
  shortcut?: number;
  icon: typeof LayoutGrid;
}

// Core tools from the design handoff (1–9)
export const CORE_TOOLS: Tool[] = [
  { id: 'formats',      label: 'Formats',       shortcut: 1, icon: LayoutGrid },
  { id: 'nearest',      label: 'Nearest',       shortcut: 2, icon: Search },
  { id: 'encyclopedia', label: 'Encyclopedia',  shortcut: 3, icon: BookOpen },
  { id: 'print',        label: 'Print & ICC',   shortcut: 4, icon: Printer },
  { id: 'semantic',     label: 'Semantic',      shortcut: 5, icon: AlignLeft },
  { id: 'compare',      label: 'Compare',       shortcut: 6, icon: GitCompare },
  { id: 'history',      label: 'History',       shortcut: 7, icon: Clock },
  { id: 'favorites',    label: 'Favorites',     shortcut: 8, icon: Star },
];

// Additional features from the existing app
export const EXTRA_TOOLS: Tool[] = [
  { id: 'palette',       label: 'Palette',       icon: Palette },
  { id: 'gradient',      label: 'Gradient',      icon: Blend },
  { id: 'extract',       label: 'Extract',       icon: Camera },
  { id: 'audit',         label: 'Audit',         icon: ShieldCheck },
  { id: 'substrate',     label: 'Substrate',     icon: Droplet },
  { id: 'score',          label: 'Score',         icon: Award },
  { id: 'export',         label: 'Export',        icon: FileDown },
  { id: 'accessibility', label: 'Accessibility', icon: Eye },
  { id: 'tokens',        label: 'Tokens',        icon: Layers },
  { id: 'education',     label: 'Education',     icon: GraduationCap },
  { id: 'settings',      label: 'Settings',      icon: Settings },
];

export const ALL_TOOLS = [...CORE_TOOLS, ...EXTRA_TOOLS];

interface ToolRailProps {
  density: NavDensity;
}

function ToolItem({
  tool, isActive, isCompact, onClick,
}: {
  tool: Tool; isActive: boolean; isCompact: boolean; onClick: () => void;
}) {
  const Icon = tool.icon;
  return (
    <button
      role="tab"
      aria-selected={isActive}
      title={isCompact ? tool.label : undefined}
      className={`tool-rail__item${isCompact ? ' tool-rail__item--compact' : ''}${isActive ? ' tool-rail__item--active' : ''}`}
      onClick={onClick}
    >
      {isActive && !isCompact && <span className="tool-rail__active-stripe" />}
      <Icon size={15} />
      {!isCompact && (
        <>
          <span style={{ flex: 1 }}>{tool.label}</span>
          {tool.shortcut && <span className="tool-rail__shortcut">{tool.shortcut}</span>}
        </>
      )}
    </button>
  );
}

export function ToolRail({ density }: ToolRailProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const activeId = location.pathname === '/' ? 'home' : location.pathname.replace('/', '');

  const handleNav = useCallback((id: ToolId) => {
    navigate(id === 'home' ? '/' : `/${id}`);
  }, [navigate]);

  // 1–9 keyboard shortcuts for core tools
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= 9) {
        const tool = CORE_TOOLS[num - 1];
        if (tool) {
          e.preventDefault();
          handleNav(tool.id);
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleNav]);

  // Tabs variant (horizontal)
  if (density === 'tabs') {
    return (
      <div className="tool-tabs" role="tablist" aria-label="Tools">
        {ALL_TOOLS.map((t) => {
          const Icon = t.icon;
          const isActive = activeId === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              className={`tool-tabs__item${isActive ? ' tool-tabs__item--active' : ''}`}
              onClick={() => handleNav(t.id)}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>
    );
  }

  // Sidebar / compact rail variant
  const isCompact = density === 'compact';

  return (
    <nav
      className={`tool-rail ${isCompact ? 'tool-rail--compact' : 'tool-rail--sidebar'}`}
      aria-label="Tools"
    >
      {/* Home entry — always first, visually separated */}
      <ToolItem
        tool={{ id: 'home', label: 'Home', icon: Home }}
        isActive={activeId === 'home'}
        isCompact={isCompact}
        onClick={() => handleNav('home')}
      />
      <div className="tool-rail__divider" />
      {!isCompact && (
        <div className="tool-rail__section-label">Tools</div>
      )}
      {CORE_TOOLS.map((t) => (
        <ToolItem
          key={t.id}
          tool={t}
          isActive={activeId === t.id}
          isCompact={isCompact}
          onClick={() => handleNav(t.id)}
        />
      ))}

      {/* Separator + extra tools */}
      <div className="tool-rail__divider" />
      {!isCompact && (
        <div className="tool-rail__section-label">More</div>
      )}
      {EXTRA_TOOLS.map((t) => (
        <ToolItem
          key={t.id}
          tool={t}
          isActive={activeId === t.id}
          isCompact={isCompact}
          onClick={() => handleNav(t.id)}
        />
      ))}
    </nav>
  );
}
